<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      :valueFormatter="formatCurrency"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { executeAndProcessQuery } from '@/utils/stackedBarData';
import { sqlLiteral } from '@/utils/assetFields';
import { formatCurrency } from '@/utils/formatters';
import { getSequential } from '@/utils/vizPalette';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'installationCostChart';
const chartTitle = t('installationCostByCategory');

// Total installation cost per level, drilling Category -> System -> Type.
// The base chart stacks by status colour; cost has no status dimension, so
// each bar carries a single segment in the sequential ramp.
const costLevel = (table, { group, parent = null, type = null }) => `
  SELECT
    a.${group} AS id,
    a.${group} AS title,
    ${parent ? `a.${parent} AS parentId,` : ''}
    ${type ? `a.${type} AS type,` : ''}
    ${sqlLiteral(t('installationCost'))} AS status_title,
    ${sqlLiteral(getSequential(store.state.theme)[2])} AS color,
    CAST(SUM(a.installationCost) AS DOUBLE) AS value,
    0 AS sort
  FROM ${table} a
  WHERE a.${group} IS NOT NULL AND a.installationCost IS NOT NULL
  GROUP BY a.${group}${parent ? `, a.${parent}` : ''}${type ? `, a.${type}` : ''}
  ORDER BY value DESC
`;

const table = (filtered) => (filtered ? 'filteredAssets' : 'unique_assets');

const dataProviders = {
  category: (filtered = true) => executeAndProcessQuery(conn, costLevel(table(filtered), { group: 'assetCategory' })),
  system: (filtered = true) => executeAndProcessQuery(conn, costLevel(table(filtered), { group: 'assetSystem', parent: 'assetCategory' })),
  // `type` is passed through to the updater as the third argument, so the leaf
  // filter can stay scoped to the category it was drilled from.
  type: (filtered = true) => executeAndProcessQuery(conn, costLevel(table(filtered), { group: 'assetType', parent: 'assetSystem', type: 'assetCategory' })),
};

const applyFilter = (condition, label) =>
  filterContext.value.updateFilterWithCondition(chartName, condition, label);

const contextUpdaters = {
  category: (category) => applyFilter(`assetCategory = ${sqlLiteral(category)}`, `${t('category')}: ${category}`),
  system: (system, category) => applyFilter(
    [`assetSystem = ${sqlLiteral(system)}`, category ? `assetCategory = ${sqlLiteral(category)}` : null]
      .filter(Boolean).join(' AND '),
    `${t('assetSystem')}: ${system}`
  ),
  type: (type, system, category) => applyFilter(
    [
      `assetType = ${sqlLiteral(type)}`,
      system ? `assetSystem = ${sqlLiteral(system)}` : null,
      category ? `assetCategory = ${sqlLiteral(category)}` : null,
    ].filter(Boolean).join(' AND '),
    `${t('assetType')}: ${type}`
  ),
  reset: async () => { await filterContext.value.removeFilter(chartName); },
};

const navigationConfig = [
  { level: 'category', titleKey: 'category' },
  { level: 'system', titleKey: 'assetSystem' },
  { level: 'type', titleKey: 'assetType' },
];
</script>
